import { type IntegrationsCapabilityModeEnum } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  type SyncStatusColumn,
  type SyncStatusColumnItem,
  usePrivateConditionalIntegrationsSyncContext,
} from 'modules/integrations/hooks/SyncContext'
import {
  type MergeableStatuses,
  type MergedStatus,
  mergeStatuses,
} from 'modules/integrations/lib/syncableStatus'
import {
  getSyncStatusTagColor,
  getSyncStatusTagLabel,
  type SyncStatusMode,
} from 'modules/integrations/lib/syncStatusTag'

type GetStatusesFromSyncable = (syncable: SyncStatusColumnItem) => MergeableStatuses | undefined

interface UseSyncColumnOptions {
  getStatusesFromSyncable: GetStatusesFromSyncable
  disabled?: boolean
}

export function useSyncColumn(options: UseSyncColumnOptions): SyncStatusColumn[] {
  const context = usePrivateConditionalIntegrationsSyncContext(options.disabled)
  const syncEnabled = context?.syncEnabled ?? false

  const FILE_MODE: IntegrationsCapabilityModeEnum = 'file'
  const isExportMode = context?.capabilityMode === FILE_MODE
  const syncStatusMode: SyncStatusMode = isExportMode ? 'file' : 'api'

  const syncableStateStatus = (syncable: SyncStatusColumnItem): MergedStatus | null => {
    if (!syncEnabled || !syncable) return null

    const statuses = options.getStatusesFromSyncable(syncable)

    return mergeStatuses(statuses)
  }

  if (!syncEnabled) return []

  const exportLabel = i18n.t('integrations.columns.export_status_title')
  const syncLabel = i18n.t('integrations.columns.sync_status_title')

  return [
    {
      label: isExportMode ? exportLabel : syncLabel,
      render: (item: SyncStatusColumnItem) => {
        const status = syncableStateStatus(item)
        // if status is null means the item is not syncable
        // so we return undefined to not display a status value
        if (status === null) return undefined

        return {
          type: 'status' as const,
          value: {
            status: getSyncStatusTagColor(status),
            label: getSyncStatusTagLabel(status, syncStatusMode),
          },
        }
      },
    },
  ]
}
